<template>
  <div
    class="overview-root"
    :class="{ 'is-mobile': isMobile, 'overview-root--dark': isDarkTheme }"
  >
    <!-- スマホ幅はカラムをタブ切替にする -->
    <v-tabs
      v-if="isMobile"
      v-model="mobileTab"
      grow
      height="42"
      class="overview-mobile-tabs"
    >
      <v-tab>お客様</v-tab>
      <v-tab>セッション</v-tab>
      <v-tab>次の延長</v-tab>
    </v-tabs>

    <div class="overview-columns">
      <div
        v-show="!isMobile || mobileTab === 0"
        class="overview-col"
      >
        <customer-column
          :table-id="tableId"
          :table-number="tableNumber"
          :table-prefix="tablePrefix"
          :table-suffix="tableSuffix"
          :custom-name="customName"
          :start-at="startAt"
          :customer-count="customerCount"
          :total="total"
          :customer-table-taggings="customerTableTaggings"
          :customer-ids="customerIds"
          :courses="courses"
          :self-order-status="selfOrderStatus"
          :hall-name="hallName"
          :deposits="deposits"
          @updated:table="$emit('updated:table', $event)"
          @updated:vending-histories="$emit('updated:vending-histories', $event)"
          @updated:referring-histories="$emit('updated:referring-histories', $event)"
        />
      </div>

      <div
        v-show="!isMobile || mobileTab === 1"
        class="overview-col"
      >
        <session-column
          :table-id="tableId"
          :today="today"
          :start-at="startAt"
          :expire-at="expireAt"
          :auto-extension-enabled="autoExtensionEnabled"
          :auto-extension-course-name="autoExtensionCourseName"
          :subtotal="subtotal"
          :total="total"
          @updated:table="$emit('updated:table', $event)"
          @updated:vending-histories="$emit('updated:vending-histories', $event)"
        />
      </div>

      <div
        v-show="!isMobile || mobileTab === 2"
        class="overview-col"
      >
        <extension-column
          :table-id="tableId"
          :table-number="tableNumber"
          :table-prefix="tablePrefix"
          :table-suffix="tableSuffix"
          :custom-name="customName"
          :start-at="startAt"
          :expire-at="expireAt"
          :auto-extension-enabled="autoExtensionEnabled"
          :auto-extension-course-name="autoExtensionCourseName"
          :customer-count="customerCount"
          :total="total"
          :paid-in-advance-amount="paidInAdvanceAmount"
          :total-deposit-amount="totalDepositAmount"
          :hall-name="hallName"
          :extension-plans="extensionPlans"
          @updated:table="$emit('updated:table', $event)"
          @updated:vending-histories="$emit('updated:vending-histories', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, getCurrentInstance } from '@vue/composition-api'
import CustomerColumn from './CustomerColumn.vue'
import SessionColumn from './SessionColumn.vue'
import ExtensionColumn from './ExtensionColumn.vue'

export default {
  components: {
    CustomerColumn,
    SessionColumn,
    ExtensionColumn,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    customName: {
      type: String,
      default: '',
    },
    today: {
      type: String,
      required: true,
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      required: false,
      default: null,
    },
    autoExtensionEnabled: {
      type: Boolean,
      default: false,
    },
    autoExtensionCourseName: {
      type: String,
      default: null,
    },
    customerCount: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    subtotal: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    total: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    paidInAdvanceAmount: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    currentNominations: {
      // NOTE: 中身は今生きてるNominationのvending_history
      // 互換性のため残している（新レイアウトでは指名はセット・延長一覧の同時注文に表示。LegacyOverviewが使用）
      type: Array,
      default: () => [],
    },
    courseOverview: {
      // NOTE: 中身はその卓で一番終わるまでの時間が長いコースのvendingHistory
      // 互換性のため残している（新レイアウトではvendingHistoriesから導出する）
      type: Object,
      default: () => {},
    },
    customerTableTaggings: {
      type: Array,
      default: () => [],
    },
    customerIds: {
      type: Array,
      default: () => [],
    },
    courses: {
      type: Array,
      default: () => [],
    },
    selfOrderStatus: {
      type: String,
      default: null,
    },
    hallName: {
      type: String,
      default: '',
    },
    deposits: {
      type: Array,
      default: () => [],
    },
    extensionPlans: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy

    const mobileTab = ref(1) // NOTE: スマホでは最初にセッションを見せる

    const isMobile = computed(() => vm.$vuetify.breakpoint.xsOnly)

    // アプリのテーマ設定に3カラムの配色を追従させる（既定はライト配色）
    const isDarkTheme = computed(() => vm.$vuetify.theme.dark)

    const totalDepositAmount = computed(() => {
      return props.deposits.reduce((sum, deposit) => sum + (deposit.amount || 0), 0)
    })

    return {
      mobileTab,
      isMobile,
      isDarkTheme,
      totalDepositAmount,
    }
  },
}
</script>

<style lang="scss" scoped>
.overview-root {
  // NOTE: 3カラムの配色はここのCSS変数に集約（color-pack無効のため）。
  // 既定はライト配色。$vuetify.theme.dark 連動の .overview-root--dark で
  // v8プロトタイプのダークトーンに切り替わる。
  --overview-bg: #eef0f6;
  --overview-surface-1: #ffffff;
  --overview-surface-2: #f5f6fa;
  --overview-surface-3: #e8e9f2;
  --overview-border: rgba(28, 28, 46, 0.1);
  --overview-text-1: #23232e;
  --overview-text-3: #74748c;
  --overview-accent: #7c3aed;
  --overview-accent-soft: rgba(124, 58, 237, 0.16);
  --overview-accent-softer: rgba(124, 58, 237, 0.08);
  --overview-emerald: #059669;
  --overview-emerald-soft: rgba(5, 150, 105, 0.13);
  --overview-rose: #e11d48;
  --overview-ring-track: rgba(28, 28, 46, 0.1);

  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--overview-bg);
  color: var(--overview-text-1);
  border-radius: 12px;
}

.overview-root--dark {
  // NOTE: Yonarezi(Materio)のダークテーマに合わせた紫紺系。黒は基調にしない。
  // 背景/カードは preset の 'background' #28243d / 'cards' #312d4b、
  // 文字・罫線は rgba(231,227,252,α)、アクセントはテーマの primary/success/error に揃える
  --overview-bg: #28243d;
  --overview-surface-1: #312d4b;
  --overview-surface-2: #3a3559;
  --overview-surface-3: #444060;
  --overview-border: rgba(231, 227, 252, 0.14);
  --overview-text-1: rgba(231, 227, 252, 0.87);
  --overview-text-3: rgba(231, 227, 252, 0.55);
  --overview-accent: #9155fd;
  --overview-accent-soft: rgba(145, 85, 253, 0.3);
  --overview-accent-softer: rgba(145, 85, 253, 0.18);
  --overview-emerald: #56ca00;
  --overview-emerald-soft: rgba(86, 202, 0, 0.18);
  --overview-rose: #ff4c51;
  --overview-ring-track: rgba(231, 227, 252, 0.14);
}

.overview-columns {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr) 380px;
  gap: 0;
}

.overview-col {
  position: relative;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  padding: 16px 16px 4px;
  border-right: 1px solid var(--overview-border);

  &:last-child {
    border-right: none;
  }

  // NOTE: スクロール可能であることのヒント（下端フェード）
  &::after {
    content: '';
    display: block;
    position: sticky;
    bottom: -4px;
    height: 28px;
    margin: 0 -16px;
    background: linear-gradient(to bottom, transparent, var(--overview-bg));
    pointer-events: none;
  }
}

// スマホ幅: 1カラム（タブ切替）
.is-mobile .overview-columns {
  grid-template-columns: 1fr;
}

.is-mobile .overview-col {
  border-right: none;
}

.overview-mobile-tabs {
  flex: 0 0 auto;
}

// NOTE: カラム内の各セクション見出し（子コンポーネントで共用）
::v-deep .overview-sec {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--overview-text-3);
  margin-bottom: 10px;
}

// NOTE: ダーク地の上でVuetifyのカードが浮かないように透明化
::v-deep .v-card {
  background-color: transparent !important;
}
</style>
